import { api } from '../api.js';
import { escapeHtml, showToast } from '../utils.js';
import { openViewer } from '../components/viewer.js';
import { tileMenuHtml, closeTileMenus, toggleTileMenu, bindTileMenuDismissal } from '../components/tile-menu.js';

/**
 * Photos other people have shared, either with everyone (images.shared) or with this
 * user specifically (image_shares). The API merges both into one list.
 */

let images = [];

export async function renderShared() {
    document.getElementById('app').innerHTML = `
        <div class="container gallery-container">
            <div class="gallery-header">
                <h1 class="gallery-title">Shared with me</h1>
            </div>
            <div id="shared-body"><p class="text-muted">Loading...</p></div>
        </div>`;

    const body = document.getElementById('shared-body');

    try {
        const res = await api.listSharedImages();
        images = (res.images ?? []).filter((i) => i.status === 'ready');
    } catch (err) {
        body.innerHTML = `<div class="error-state"><p>${escapeHtml(err.message ?? 'Could not load shared photos')}</p></div>`;
        return;
    }

    if (!images.length) {
        body.innerHTML = `
            <div class="empty-state">
                <i class="fa-solid fa-share-nodes"></i>
                <h2>Nothing shared yet</h2>
                <p>Photos other people share with you will appear here.</p>
            </div>`;
        return;
    }

    body.innerHTML = `<div class="gallery-grid">${images.map(tileHtml).join('')}</div>`;
    body.querySelectorAll('.gallery-item').forEach(bindTile);
    bindTileMenuDismissal();
}

function tileHtml(img) {
    const caption = img.caption?.trim();
    return `
        <div class="gallery-item" data-id="${img.id}">
            <div class="image-wrapper" data-action="view" role="button" tabindex="0">
                <img class="gallery-thumb" src="${escapeHtml(img.thumbnail_url ?? '')}"
                     alt="${escapeHtml(caption ?? 'Photo')}" loading="lazy">
            </div>
            <div class="image-info">
                <div class="image-info-text">
                    <div class="image-caption ${caption ? '' : 'image-no-caption'}">
                        ${escapeHtml(caption || 'No caption')}
                    </div>
                    <div class="image-meta">
                        ${img.owner_name ? `<span class="text-muted">from ${escapeHtml(img.owner_name)}</span>` : ''}
                    </div>
                </div>
                ${tileMenuHtml([
                    { action: 'copy', icon: 'fa-copy', label: 'Copy to my gallery' },
                    { action: 'download', icon: 'fa-download', label: 'Download original' },
                ])}
            </div>
        </div>`;
}

function bindTile(tile) {
    const id = Number(tile.dataset.id);

    tile.addEventListener('click', async (e) => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (!action) return;
        e.stopPropagation();

        const img = images.find((i) => i.id === id);
        if (!img) return;

        if (action === 'menu') {
            toggleTileMenu(tile);
            return;
        }

        // Any real action closes the menu it was chosen from.
        closeTileMenus();

        if (action === 'view') {
            openViewer(images, images.findIndex((i) => i.id === id));
        } else if (action === 'copy') {
            try {
                await api.copyImage(img.id);
                // The copy re-enters the derivative pipeline under the copier's own
                // prefix, so it lands as 'pending' and appears in their gallery shortly.
                showToast('Saved to your photos — processing now', 'success');
            } catch (err) {
                showToast(err.message ?? 'Could not save that photo', 'error');
            }
        } else if (action === 'download') {
            try {
                const { url } = await api.getDownloadUrl(img.id);
                window.location.href = url;
            } catch (err) {
                showToast(err.message ?? 'Could not prepare that download', 'error');
            }
        }
    });
}
